import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Field, inputCls, btnPrimary } from "@/components/Modal";
import { Upload, Loader2, Save } from "lucide-react";

export default function Settings() {
  const { loadSettings } = useData();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      await loadSettings();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
    }
  };

  if (!s) return <div className="p-6 text-[#6B7280]">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl pb-16">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold">Restaurant Settings</h1>
          <p className="text-sm text-[#6B7280]">These details appear on every invoice</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className={btnPrimary + " flex items-center gap-2"}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>

      <div className="space-y-6">
        <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Business Info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Restaurant Name"><input className={inputCls} value={s.restaurant_name || ""} data-testid="setting-name" onChange={(e) => set("restaurant_name", e.target.value)} /></Field>
            <Field label="Phone"><input className={inputCls} value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email"><input className={inputCls} value={s.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="GSTIN"><input className={inputCls} value={s.gstin || ""} data-testid="setting-gstin" onChange={(e) => set("gstin", e.target.value)} /></Field>
            <Field label="FSSAI No."><input className={inputCls} value={s.fssai || ""} onChange={(e) => set("fssai", e.target.value)} /></Field>
            <div className="sm:col-span-2">
              <Field label="Address"><textarea className={inputCls} rows={2} value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            </div>
          </div>
          <div className="mt-4">
            <Field label="Logo">
              <div className="flex items-center gap-4">
                {s.logo_url ? (
                  <img src={fileUrl(s.logo_url)} alt="logo" className="w-16 h-16 rounded-lg object-cover border border-[#23334D]" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#0B0F17] border border-[#23334D] flex items-center justify-center text-[#4B5563] text-xs">No logo</div>
                )}
                <label className="cursor-pointer bg-[#1E2C45] hover:bg-[#263657] text-sm text-white px-4 py-2.5 rounded-lg flex items-center gap-2">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Logo
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-upload" onChange={onUpload} />
                </label>
              </div>
            </Field>
          </div>
        </div>

        <div className="bg-[#151D2A] border border-[#23334D] rounded-xl p-5">
          <h2 className="font-head font-semibold mb-4">Tax & Invoice</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Default GST %"><input type="number" className={inputCls} value={s.default_gst} data-testid="setting-gst" onChange={(e) => set("default_gst", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="Tax Type">
              <select className={inputCls} value={s.tax_type} data-testid="setting-tax-type" onChange={(e) => set("tax_type", e.target.value)}>
                <option value="cgst_sgst">CGST + SGST (Intrastate)</option>
                <option value="igst">IGST (Interstate)</option>
              </select>
            </Field>
            <Field label="Invoice Prefix"><input className={inputCls} value={s.invoice_prefix} data-testid="setting-prefix" onChange={(e) => set("invoice_prefix", e.target.value)} /></Field>
            <Field label="Currency"><input className={inputCls} value={s.currency} onChange={(e) => set("currency", e.target.value)} /></Field>
            <div className="sm:col-span-2">
              <Field label="Invoice Footer"><input className={inputCls} value={s.invoice_footer || ""} onChange={(e) => set("invoice_footer", e.target.value)} /></Field>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 pt-1">
              <input type="checkbox" id="acd" checked={!!s.allow_cashier_discount} data-testid="setting-allow-discount" onChange={(e) => set("allow_cashier_discount", e.target.checked)} className="w-4 h-4 accent-[#10B981]" />
              <label htmlFor="acd" className="text-sm text-[#9CA3AF]">Allow cashiers to apply discounts</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
